/** @module csi/heading/Renderer */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dijit/ProgressBar",
	"csi",
	"csi/_base/underscore",
	"csi/def",
	"csi/def/Action",
	"csi/def/Heading",
	"csi/heading/_base",
	"csi/heading/ActionData",
	"csi/heading/AttributeEdit",
	"csi/heading/AttributeMonitor",
	"csi/html",
	"csi/locale",
	"csi/reflectionHints",
	"csi/service/viewService",
	"csi/utility/coreUtility",
	"csi/utility/routerUtility",
	"csi/widget/Tooltip",
	"csi/xml"
], function(declare, lang, domClass, domConstruct, ProgressBar, csi, _, def, Action, Heading, csiHeading, ActionData, AttributeEdit, AttributeMonitor, html, locale,
	reflectionHints, {attributeIsInlineEditable}, {isDefined}, {LOCATION_DISABLE_TAB_MATCHING}, Tooltip, xml) {

	var RE_CSS_FORMAT = /[a-zA-Z]+/;
	// NOTE: EDIT_CONTROLLERS_BY_TYPE is a temporary solution that is necessary for Edit actions in
	// tooltips to use the proper controller class. The framework needs to be changed so that the
	// Edit action in the Relationship is properly used in the tooltip as well. One possibility is
	// for the Edit action definition to move up to the Node(Ref) tag level.
	var EDIT_CONTROLLERS_BY_TYPE = {
		CameraPosition: "Apparel.EditCameraPosition",
		Category1: "Apparel.EditStructureItem",
		Category2: "Apparel.EditStructureItem",
		Collection: "Apparel.EditStructureItem",
		ColorMaterial: "Apparel.EditColorMaterial",
		Colorway: "Apparel.EditColorway",
		MaterialSample: "Apparel.EditMaterialSample",
		MaterialSnapshotTemplate: "Apparel.EditSnapshotTemplate",
		Sample: "Apparel.EditProductSample",
		SpecificationItemDefinition: "SiteAdmin.Lookup.EditSpecificationItem",
		StyleSnapshotTemplate: "Apparel.EditSnapshotTemplate",
		SupplierRequest: "Sourcing.EditSupplierRequest",
		TestRun: "Quality.EditTestRun_ProductColor"
	};

	/**
	 * Determine if subheadings should be processed for given heading and context
	 * @param {Context} ctx
	 * @param {Heading} h
	 * @return {boolean}
	 */
	function _canRenderSubheadings(ctx, h) {
		return h.m_subheadings && !h.isMatrixGroup && !ctx.bCard && !ctx.bReporting;
	}

	return csiHeading.defaultRendererConstructor = declare(null, {
		/** @property {boolean} allowEmpty - set to true if it is ok for this renderer class to leave a cell empty */
		//allowEmpty: false,

		// Allow custom options in react inline editing
		//reactAllowCustomOptions: false,

		// String: CSS class for heading
		//css: null,

		// Any: default value for renderer
		//defaultValue: null,

		// String: either attribute type or node property
		//type: null,

		/** @property {boolean} bAllowReporting - by default renderer classes are not used for report rows, including groups; this setting can override that */
		//bAllowReporting: false,

		// Boolean: indicates that this is the base renderer rather than the container renderer;
		// use when RendererClass is specified for container attribute but solely to render elements of the container
		//bBaseRenderer: null,

		// value to be used with FILTER_BLANK in ad hoc filter. If this value is defined, operation EQ/NE with this value will be used for blanks/not blanks
		//blankValue: null,

		// String: attribute id to monitor for status changes when heading is rendered
		//idStatus: null,

		// Number: indicates number of lines to show for renderers using InlineTextArea for inline editing
		//nTextareaLines: 0,

		// React inline editing component placeholder resource if not standard
		//reactPlaceholderRes: "",

		// Renderer: base type renderer for container attributes (list, map, vector, set)
		//renBase: null,

		// String: tag used for action (optional)
		//tagAction: null,

		// Function(/*Context*/ctx, /*Heading*/h, /*Any*/value): function to return CSS class (if any) for given filter value
		//toFilterValueCSS: null,

		// String[]: array of preferred positions for tooltip - default is ["right","left"]
		//tooltipPosition: null,

		constructor: function(/*String*/type, /*String?*/format) {
			// summary: initialize members
			if (type) {
				this.type = type;
			}

			this.css = this._getCSS(this.type, format);
		},

		/**
		 * Add any dependency paths required by renderer class
		 * @param {Heading} h
		 * @param {string[]} paths
		 */
		addRendererDependencyPaths: function(h, paths) { },

		canBeLarge: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return 0 if not large, 1 if could be large, and 2 if forcing to be large;
			return 0;
		},

		canCopy: function(/*Context*/ctxCopy, /*Heading*/h) {
			// summary: indicates if value in cell can be copied to other cells in the column
			return false;
		},

		/**
		 * Return true if heading can be edited by dragging from one value group to another (such as in gallery views);
		 * by default, it is only allowed if attribute can be inline edited
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {boolean}
		 */
		canEditByDragAndDrop(ctx, h) {
			const ad = this._getRenderAction(ctx, h);
			return ad && !ad.bTooltip;
		},

		canLink: function() {
			// summary: indicates if content can act as anchor
			return true;
		},

		canPaste: function(/*Context*/ctxCopy, /*Context*/ctxPaste, /*Heading*/h) {
			// summary: indicates if value can be pasted to given cell in context
			return false;
		},

		/**
		 * Return true if cell is available for copy/paste
		 * @param {Context} ctx
		 * @param {HTMLElement} refNode
		 * @return {boolean}
		 */
		canPasteToCell(ctx, refNode) {
			return false;
		},

		formatValue: function(/*Any*/value, /*String?*/fmt) {
			// summary: convert value to displayed value based on format
			return value;
		},

		formatValueForFiltering: function(/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: return formatted text for filter values which by default is same as toValue
			// FIX: possibly try just calling formatValue on given value, but for now keep same as before
			return this.toValue(ctx, h);
		},

		/**
		 * Convert value to one used for sorting; return null if not resolved
		 * @param {any} value
		 * @param {string} [format]
		 * @param {string} [formattedValue]
		 * @return {any}
		 */
		formatValueForSorting: function(value, format, formattedValue) {
			if ((value != null) && this._toSortValue) {
				value = this._toSortValue(value);
			}

			return value;
		},

		/**
		 * Use either custom action for heading or the default action coming from the renderer class
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {Action}
		 */
		getAction(ctx, h) {
			const {ownerType = "", rc} = h;
			let a = h.rendererActionByType[ownerType];

			if (a === undefined) {
				const ha = h.m_action;

				if (ha?.isDerived()) {
					// if heading has custom action, then use it
					a = ha;
					a.heading = h;
				} else {
					// otherwise, use renderer action
					a = this._getRenderActionForHeading(ctx, h) ?? null;

					if (a) {
						// mixin properties from heading action; right can also be specified on Heading, so make sure to take that into consideration too
						a.mixinConfigParameters(ha);
						a.heading = h;
						a.m_right = h.getRight() || a.m_right;
						a.resolve(null, rc);
					}
				}

				if (a) {
					a.editableOnFrozenNode = this.editableOnFrozenNode;
				}

				h.rendererActionByType[ownerType] = a;
			}

			return a;
		},

		getDefaultValue: function(/*Heading*/h) {
			// summary: return default value for heading
			return this.defaultValue;
		},

		getDoubleClickAction: function(/*Context*/ctx, /*Heading*/h) {
			// summary: extension point for derived renderers at this point
			return null;
		},

		getBaseRenderer: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return base renderer according to context
			return this.renBase && ctx.hasContainerKey() ? this.renBase : this;
		},

		getBaseHeading: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return base renderer according to context
			return h;
		},

		getClickNode: function(/*DOMNode*/refNode) {
			// summary: return node to which action is attached
			return refNode;
		},

		getQuerySpec: function(/*Context*/ctx, /*Heading*/h, /*Spec*/spec) {
			// summary: return Spec.querySpec for given heading
			return null;
		},

		getTooltipActions(h) {
			return (h.getRight() != xml.RIGHT_NONE) ? h.m_defActions?.getActions() : null;
		},

		/**
		 * Return parameters for tooltip when hovering over element showing heading
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {DOMNode} refNode
		 * @return {TooltipTargetParams}
		 */
		getTooltipParams: function(ctx, h, refNode) {
			return {
				connectNode: refNode,
				ctx: ctx,
				delay: ctx.view.useMuiTooltipStyling ? Tooltip.TOOLTIP_REACT_SHOW_DELAY : null,
				h: h,
				position: this.tooltipPosition,
				refNode: refNode,
				ren: this
			};
		},

		/**
		 * Return true if attribute has an aggregate store
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {boolean}
		 */
		hasAggregateStore(ctx, h) {
			return false;
		},

		hasValidDependency: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return whether or not heading dependency (if any) is valid; dependency is ignored on report rows
			return ctx.bReporting || !h.m_dependency || xml.nodeAttributeIsValueValid(ctx.getNode(), h.m_dependency);
		},

		/**
		 * indicate if should invoke editing with tab key - only possible if editing is supported
		 * and action can be inlined (don't want popups while tabbing through view)
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {boolean}
		 */
		isTabStop(ctx, h) {
			const a = this.getAction(ctx, h);
			return !!a?.canBeInlined();
		},

		/**
		 * Monitor node for changes to attribute value
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {DOMNode} refNode
		 * @param {any} value
		 * @return {boolean} - returns true if monitor is started
		 */
		monitor: function(ctx, h, refNode, value) {
			if (ctx.isStaticOutput() || !ctx.view) {
				return false;
			}

			const amc = {
				ctx: ctx.copyContext(),
				heading: h,
				idStatus: this.getIdStatus(h),
				refNode,
				ren: this,
				urlMonitor: ctx.getNodeURL(),
				value
			};

			this.onMonitorStart(amc);
			AttributeMonitor.add(amc);
			return true;
		},

		/**
		 * Called when attribute being monitored changes value; by default, just redraw cell
		 * @param {AttributeMonitorContext} amc
		 * @param {Any} newValue
		 * @return {boolean} - return true if monitor should be removed now that value has changed
		 */
		onMonitor: function(amc, newValue) {
			amc.ctx.view.refreshHeadingCell(amc.ctx, amc.heading, amc.refNode);
			return true;
		},

		/**
		 * Called when attribute being monitored has not changed value; by default, does nothing
		 * @param {AttributeMonitorContext} amc
		 */
		onMonitorProgress: function(amc) { },

		/**
		 * Called when attribute being monitored has not changed value; by default, does nothing
		 * @param {AttributeMonitorContext} amc
		 */
		onMonitorStart: function(amc) { },

		pushContext: function(/*Context*/ctx, /*Heading*/h) {
			// summary: apply any renderer-specific changes to context before use; this is called only
			// if path cannot be pushed onto the context; return true/false depending on if the context
			// can be fixed for the renderer
			return false;
		},

		reserveContext: function(/*Context*/ctx, /*Heading*/h) {
			// summary: apply any renderer-specific changes to context before use; at his point heading has already
			// applied path as context, so no push necessary by default
		},

		releaseContext: function(/*Context*/ctx, /*Heading*/h) {
			// summary: pop heading context as well as any other changes by renderer
			ctx.pop();
		},

		/**
		 * Renderers by default do not support copy (drag-fill)
		 * @param {Heading} h
		 * @return {boolean}
		 */
		restrictsCopy(h) {
			return true;
		},

		validateValue: function(/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// extension point for validating a heading value given a context
			// any errors will be thrown as exceptions
		},

		headingToText: function(/*Context*/ctx, /*Heading*/h) {
			// summary: render heading and sub-headings to text
			var str = this.toValue(ctx, h);

			if (_canRenderSubheadings(ctx, h)) {
				_.each(h.m_subheadings, function(sh) {
					if (sh.m_separator != null) {
						str += csiHeading.applyHeadingRenderer(ctx, sh, function(renBase, hBase) {
							var s = renBase.toValue(ctx, hBase);
							return s ? h.m_separator + s : "";
						});
					}
				});
			}

			return str;
		},

		/**
		 * Generate tooltip for heading by first rendering content and then optional Actions
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {Tooltip} tooltip
		 * @return {Deferred|undefined}
		 */
		headingToTooltip: async function(ctx, h, tooltip) {
			if (!this.hasValidDependency(ctx, h)) {
				return;
			}
			this.getTooltipActions(h)?.forEach((action) => tooltip.addAction(action));

			return this.toTooltip(ctx, h, tooltip);
		},

		headingToHtml: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			// summary: render heading Html under given refNode
			var node = ctx.getNode();

			// determine styling for heading; for native export, do not apply any extra CSS as it is just focusing on the data
			if (!ctx.bNativeExport) {
				var css = def.getCSS(h, this._getRenderCSS(ctx, h, refNode), node, ctx.cache, ctx);

				html.setClass(refNode, css);

				// blank out elements that are marked with "hideFromView" class and make non-editable - blank out so that value is really empty rather than just hidden by CSS
				if (css && _.contains(css.split(" "), html.CSS_HIDE)) {
					refNode.innerHTML = csi.EMPTY_CHAR;
					return;
				}
			}

			// mark DOM with context
			csiHeading.markHeadingNode(refNode, h, ctx);

			// FIX: should this be added to ctx.applyNodeContext?
			if (ctx.hasContainerKey()) {
				csiHeading.setHeadingValueKey(refNode, ctx.keyValue);
			}

			// render heading if context and dependencies are valid
			var renNode = refNode;
			var actionData;

			if (this.hasValidDependency(ctx, h)) {
				// determine if subheadings are to be rendered
				const renderSubheadings = _canRenderSubheadings(ctx, h);

				// render value as html - if there is content already in refNode (such as hierarchy controls
				// or aggregate checkbox), then create span for heading content
				if (refNode.firstChild || renderSubheadings) {
					renNode = domConstruct.create("span", {"class": csiHeading.CSS_PRIMARY}, refNode);
				}

				// determine action for heading on refNode - context will track whether or not renderer handles onclick behavior
				if (ctx.allowActions(this.bAllowReporting)) {
					actionData = this._getRenderAction(ctx, h);
				}

				// render main heading
				this._toHtml(ctx, h, renNode, actionData);

				// render subheadings
				if (renderSubheadings) {
					var renActionData, i = 0;
					var sh;

					while (sh = h.m_subheadings[i++]) {
						// skip if heading hidden or does not have separator
						if (!sh.m_bHidden && (sh.m_separator != null)) {
							renActionData = this._toHtmlSub(ctx, sh, refNode);

							// if sub-heading is actionable and main heading is not, and sub-heading context is same as main heading, then clicking cell will edit sub-heading
							if (renActionData && !actionData) {
								var renUrl = html.getElementContext(renActionData.clickNode, true);
								if (renUrl == ctx.getNodeURL()) {
									actionData = renActionData;
									actionData.setClickNode(null);
								}
							}
						}
					}
				}

				// set action context to first actionable heading key - whether it is root heading or sub-heading
				// unless renderer produces specific onclick node
				if (actionData && !actionData.clickNode && !ctx.bPrint) {
					if (actionData.bTooltip) {
						// if cell action is relegated to tooltip, then still add editable styling, but disable tab-stop
						csiHeading.addHeadingNodeEditableStyling(refNode);
						csiHeading.disableHeadingTabStop(refNode);
					} else {
						// mark cell as editable and indicate if not inline editable
						csiHeading.markHeadingEditable(refNode, actionData.heading);

						if (!actionData.ren.isTabStop(ctx, actionData.heading)) {
							csiHeading.disableHeadingTabStop(refNode);
						}
					}

					// add extra edit styling in some cases
					if (actionData.cssEdit) {
						domClass.add(refNode, actionData.cssEdit);
					}
				}

				// Mark cell editable if it has tooltip actions with write or admin access
				if (!actionData && h.m_defActions && !ctx.bPrint) {
					var actions = this.getTooltipActions(h);
					if (_.some(actions, a => {
						return (a.hasAccess(ctx)) && ((a.m_right == xml.RIGHT_WRITE) || (a.m_right == xml.RIGHT_ADMIN));
					})) {
						csiHeading.addHeadingNodeEditableStyling(refNode);
					}
				}
			}

			// finally, execute draw callback if any
			var fOnDraw = csi.resolveFunction(h.m_onDraw, ctx.view);
			if (fOnDraw) {
				fOnDraw(ctx, h, refNode, this);
			}

			// nothing more to do if exporting to Excel; it should not contain placeholders or empty characters
			if (ctx.bExcel) {
				return;
			}

			// check for EMPTY_CHAR as some places still output this when no other content
			var bEmpty = html.isEmpty(renNode);
			var labelClass;

			if (bEmpty) {
				// make sure there is content even when empty, unless so configured
				if (!this.allowEmpty) {
					renNode.innerHTML = csi.EMPTY_CHAR;
				}

				var bAllowPlaceholder = this._renderPlaceholder(h);
				if (bAllowPlaceholder && actionData) {
					// show placeholder if so configured if content is empty but editable
					labelClass = "placeholder";
				} else if (!actionData && (bAllowPlaceholder === false)) {
					// if content is empty and not editable, then nothing more to do
					return;
				}
			}

			// show label before content - if showing label, then label takes place of placeholder
			if (h.labelSeparator) {
				labelClass = "attrLabel";
			}

			if (labelClass) {
				var lbl = h.getLabel();
				if (lbl) {
					html.create("label", {
						"class": labelClass,
						innerText: lbl + (h.labelSeparator || "")
					}, renNode, "first");
				}
			}
		},

		/**
		 * Add additional parameters for anchor hash
		 * @param {Context} ctx
		 * @param {Record<string, string>} params
		 */
		onAnchorHashParams(ctx, params) { },

		/**
		 * Whether render the placeholder or not
		 * @param {Heading} h
		 * @return {boolean}
		 */
		_renderPlaceholder: function(h) {
			return h.bPlaceholder;
		},

		_toHtmlSub: function(/*Context*/ctx, /*Heading*/sh, /*DOMNode*/refNode) {
			// summary: trigger rendering of sub-heading and return actionData
			var actionData;

			csiHeading.applyHeadingRenderer(ctx, sh, function(renBase, hBase) {
				if (renBase && renBase.hasValidDependency(ctx, sh)) {
					actionData = renBase._toHtmlAsSub(ctx, hBase, refNode, hBase.m_separator);
				}
			});

			return actionData;
		},

		_toHtmlAsSub: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*String?*/sep) {
			// summary: render as sub-heading - return actionData if any
			var renNode = domConstruct.create("span", null, refNode);
			var actionData = ctx.allowActions(this.bAllowReporting) ? this._getRenderAction(ctx, h) : null;

			// mark DOM with node context even if not actionable so it can be re-rendered if necessary
			ctx.applyNodeContext(renNode, true);
			csiHeading.setHeadingValueKey(renNode, ctx.keyValue);

			if (this._toHtml(ctx, h, renNode, actionData, true)) {
				// add separator before heading if had valid value
				if (sep) {
					html.textSeparator(sep, renNode, "before");
				}
			} else if (!actionData) {
				// if no content and no action, then destroy node
				domConstruct.destroy(renNode);
				return null;
			}

			// add styling for heading - renderer CSS may be different when rendered as sub-heading (such as text-align)
			def.processClass(h, renNode, this._getRenderCSSAsSub(ctx, h), ctx.getNode(), ctx.cache, ctx);

			// mark DOM with action context unless renderer already handled it
			if (actionData && !actionData.clickNode) {
				actionData.setClickNode(renNode, true);
			}

			return actionData;
		},

		/**
		 * Returns the status value for ProgressBar if any
		 * @param {Node} node - C8 Node
		 * @param {Heading} h
		 * @return {string|number|undefined} undefined if there is no progress bar, empty string if indeterminate progress bar, the value for dijit/ProgressBar otherwise
		 */
		_getProgressBarStatus(node, h) {
			const statusId = this.getIdStatus(h);
			if (statusId) {
				return node[statusId];
			}
		},

		_toHtml: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData, /*Boolean*/bSub) {
			// summary: render heading text - does not include CSS
			var node = ctx.getNode();

			// detect if attribute value is being processed (has .Status attribute)
			var status = node && this._getProgressBarStatus(node, h);
			if (isDefined(status)) {
				if (this.monitor(ctx, h, refNode, status)) {
					this._toHtmlStatus(ctx, h, refNode, status);
				}

				return true;
			}

			// create link to current node
			var anchor;

			if ((h.m_link != null) && this.canLink() && !reflectionHints.isSpecialPath(h.m_path)) {
				anchor = this._toHtmlAnchor(ctx, h, refNode, this._getLinkNode(ctx, h, node));
			}

			// render heading
			var bContent = this.toHtmlStatic(ctx, h, anchor || refNode, actionData);

			// if linking and content is empty, then show default text so link is shown;
			// if heading is not displaying the node name, then do not add (Unnamed) text
			if (!bContent && anchor && h.isName) {
				html.text(anchor, locale.getEmptyName());
				bContent = true;
			}

			return bContent;
		},

		/**
		 * Add the progress bar and file sizes
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {HTMLElement} refNode
		 * @param {string} status
		 */
		_toHtmlStatus(ctx, h, refNode, status) {
			new ProgressBar({
				"class": "attrMonitor",
				value: parseInt(status) || Infinity,
			}, domConstruct.create("span", null, refNode));
		},

		/**
		 * Render and return anchor for given link node
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {HTMLElement} refNode
		 * @param {C8Node} linkNode
		 * @return {HTMLElement}
		 */
		_toHtmlAnchor(ctx, h, refNode, linkNode) {
			const hashParams = {
				[LOCATION_DISABLE_TAB_MATCHING]: ctx.view?.disableTabMatching ? "1" : null,
			};

			this.onAnchorHashParams(ctx, hashParams);

			return def.createBrowseAnchor(ctx, refNode, linkNode, h.m_link, null, hashParams);
		},

		toHtmlStatic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: render value for attribute in HTML; if action exists, then add content
			// necessary to invoke edit mode (toHtmlDynamic); return false if no content
			var value = this.toValue(ctx, h);

			if ((value != null) && (value !== "")) {
				html.text(refNode, value);
				return true;
			}

			return false;
		},

		toHtmlDynamic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*Object*/params) {
			// summary: return widget for editing heading
			return null;
		},

		fromHtmlDynamic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/editNode, /*boolean*/bFromForm) {
			// summary: return attribute value from edit control
			return editNode.get ? editNode.get("value") : editNode.value;
		},

		toHtmlQueryFilter: function(/*Context*/ctx, /*Spec*/spec, /*DOMNode*/refNode, /*Object*/params, /*String*/op) {
			// summary: return widget for specifying query filter for attributes of this type and format
			return this.toHtmlDynamic(ctx, spec.heading, refNode, params);
		},

		fromHtmlQueryFilter: function(/*Context*/ctx, /*Spec*/spec, /*DOMNode*/editNode, /*String*/op) {
			// summary: return attribute value from edit control
			return this.fromHtmlDynamic(ctx, spec.heading, editNode);
		},

		/**
		 * Format attribute value for (or from) query
		 * @param {Context} ctx
		 * @param {any} value
		 * @param {string} format
		 * @param {boolean} [bFloatingDates] - indicates if time values are relative to today
		 * @param {boolean} [bSelected] - indicates if value is the selected query value
		 * @return {string}
		 */
		formatQueryFilterValue: function(ctx, value, format, bFloatingDates, bSelected) {
			return this.formatValue(value, format);
		},


		/**
		 * Present for derived classes to properly edit values
		 * @param {Context} ctx
		 * @param {Spec} spec
		 * @param {any} value
		 * @return {any}
		 */
		getClientFilteredValue: function(ctx, spec, value) {
			return value;
		},

		toFilterValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return value to be used in query when filtering by heading - return null if not resolved
			var value = this._getValue(ctx, h);
			if (this._toFilterValue && (value != null)) {
				value = this._toFilterValue(ctx, h, value);
			}
			return value;
		},

		toSortValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return value to be used when sorting by heading
			return this.formatValueForSorting(this.toFilterValue(ctx, h), this._getFormat(ctx, h));
		},

		toValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return formatted value (string) for heading in context
			return this.formatValue(this._getValue(ctx, h), this._getFormat(ctx, h));
		},

		/**
		 * Add content and actions to tooltip
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {Tooltip}
		 * @return {Deferred|undefined}
		 */
		toTooltip: function(ctx, h, tooltip) { },

		toTooltipEdit: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			// summary: return DOMNode to display as tooltip while editing
			return null;
		},

		_getAction: function() {
			// summary: create static action for editing attribute
			return null;
		},

		_getCSS: function(/*String*/type, /*String?*/format) {
			// summary: calculate static CSS class(es) used for attribute
			var s;

			if (type) {
				s = "attr" + type.substr(0, 1).toUpperCase() + type.substr(1);

				if (format && format.match(RE_CSS_FORMAT)) {
					s += " " + s + format;
				}
			}

			return s;
		},

		_getFormat: function(/*Context*/ctx, /*Heading*/h) {
			// summary: by default use format stored on heading
			return h.m_format;
		},

		/**
		 * return classes used to render icon (see heading\enum.js)
		 * @param {string} value
		 * @returns string
		 */
		_getIconClass: function(/*String*/value) {
			return "";
		},

		_getLinkNode: function(/*Context*/ctx, /*Heading*/h, /*Node*/source) {
			// summary: compute the node needed to find link view
			return h.linkPath ? ctx.cache.getPathNode(source, h.linkPath) : source;
		},

		_mixinTooltipAction: function(/*Action*/a, /*Heading*/h, /*Boolean*/bForce) {
			// summary: add access parameters from heading to tooltip action, unless forced
			if (!bForce) {
				// mixin properties of heading action to given action
				var ha = h.m_action;
				if (ha) {
					if (ha.m_accessAction) a.m_accessAction = ha.accessAction;
					if (ha.m_rules) a.m_rules = ha.m_rules;
				}

				// mixin heading right if any
				a.m_right = h.getRight();
			}

			a.heading = h;
			a.resolve();
			return a;
		},

		/**
		 * Return ActionData for rendering action with necessary settings from heading
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {ActionData}
		 */
		_getRenderAction(ctx, h) {
			if (ctx.bCard || ctx.bPrint) {
				return null;
			}

			// determine editability of attribute - if not attribute, then apply no restrictions
			const node = ctx.getNode();
			const [isEditable, formOnly] = h.m_id ? attributeIsInlineEditable(node, h.m_id, h.m_path) : [true, false];

			// return null if reflection attribute does not support inline editing
			if (!isEditable) {
				return null;
			}

			// note that this action is cached and not re-created each time, so it should not be altered
			let a = this.getAction(ctx, h);
			if (!a?.hasAccess(ctx)) {
				return null;
			}

			// determine if attribute can only be edited on the form, and if so, then rename to Edit
			if (!h.noForm && formOnly) {
				a = new AttributeEdit();
				a.classController = EDIT_CONTROLLERS_BY_TYPE[ctx.getNodeType()];
				a.heading = h;
				a.resolve();
			}

			return lang.mixin(new ActionData(), {
				action: a,
				bTooltip: !a.canBeInlined(),
				heading: h,
				ren: this
			});
		},

		/**
		 * Create action for current heading
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {Action}
		 */
		_getRenderActionForHeading(ctx, h) {
			const a = this._getAction();
			if (!a) {
				return null;
			}

			if (this.tagAction) {
				a.m_tag = this.tagAction;
			}

			return a;
		},

		/**
		 * Return CSS for current heading in context
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {DOMNode} [refNode] - example: used in csi/heading/RefImage
		 */
		_getRenderCSS: function(ctx, h, refNode) {
			return this.css;
		},

		_getRenderCSSAsSub: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return CSS for current heading in context
			return null;
		},

		_getValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: extract stored value
			return null;
		},

		_setBaseRenderer: function(/*Renderer*/renBase) {
			// summary: assign base renderer for container renderers
			this.renBase = renBase;
		},

		/** React */

		/**
		 * @param {Store} store
		 * @param {Controller} controller
		 * @param {StoreQuery} query
		 * @param {StoreQueryOptions} queryOptions
		 * @return {GetOptionsProps}
		 */
		getReactControlOptions: async function(store, controller, query, queryOptions) {
			// this function should be implemented in inherited renderer classes
			return Promise.reject(new Error("renderer.getReactControlOptions() is not implemented"));
		},

		/**
		 * @param {Controller} controller
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {GetOptionsProps}
		 */
		getReactPlaceholderOptions: function(controller, ctx, h) {
			// this function should be implemented in inherited renderer classes
			return Promise.reject(new Error("renderer.getReactPlaceholderOptions() is not implemented"));
		},

		/**
		 * @param {context} ctx
		 * @param {heading} h
		 * @return {string}
		 */
		getReactControlType: function(ctx, h) {
			return "";
		},

		/**
		 * return true if react renderer exists for given attribute type and should be used
		 */
		useReactInlineDialog() {
			return false;
		},

		/**
		 * return options which match to the value
		 * @param {ComponentItem[]} options
		 * @param {string[]} value
		 * @returns {componentItem[]}
		 */
		getValueOptions: function(options, value) {
			// create hash to optimize search
			const optionsHash = Object.fromEntries(options.map((option, idx) => [option.id, idx]));
			return value.map((val) => options[optionsHash[val]]);
		},

		/**
		 * Returns the id status string
		 * @param {Heading} h
		 * @returns {string}
		 */
		getIdStatus(h) {
			return this.idStatus;
		}

	});
});